import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { useExperience } from "../../context/ExperienceContext";
import { FrameworkControlList, StandardListState, type SoaRow, type EvidenceDoc } from "../FrameworkControlList";
import { buildInsightsMap, type ControlInsights } from "../ControlInsightsPanel";
import { frameworkAccent } from "../controlStatus";
import { StandardHeader } from "./StandardHeader";
import { isoClauseGroups } from "./isoClauses";
import { PrincipleCards, PdcaReference } from "./StandardReference";

// The clause page of an ISO management system standard (ISO 9001, ISO 45001, ISO 14001): the
// standard's principles, the required clauses 4 to 10 on the shared assessment list grouped by
// clause with each clause's key audit question as guidance, and the plan-do-check-act map.
// The pages differ only in their words, which they pass in.

export interface IsoClausePageProps {
  frameworkId: "iso9001" | "iso45001" | "iso14001";
  /** The sub-page, as the sidebar names it. */
  page: string;
  subtitle: string;
  principles: Array<{ title: string; description: string }>;
  clauseSummaries: Partial<Record<number, string>>;
  clauseTitles?: Partial<Record<number, string>>;
  auditQuestions: Record<string, string>;
  pdca: { title: string; phases: Array<{ phase: string; clauses: string; desc: string }> };
}

export function IsoClausePage({ frameworkId, page, subtitle, principles, clauseSummaries, clauseTitles, auditQuestions, pdca }: IsoClausePageProps) {
  const { novice } = useExperience();
  const [rows, setRows] = useState<SoaRow[] | null>(null);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [docs, setDocs] = useState<EvidenceDoc[]>([]);
  const [insightsMap, setInsightsMap] = useState<Map<string, ControlInsights>>(new Map());

  async function loadAll() {
    const [soaData, docsData, latestRun] = await Promise.all([
      api.get<{ soa: SoaRow[] }>("/controls/soa"),
      api.get<{ documents: EvidenceDoc[] }>("/documents"),
      api.get<{ run: { resultJson: unknown } | null }>("/guidance/latest").catch(() => ({ run: null })),
    ]);
    setRows(soaData.soa.filter((r) => r.control.frameworkId === frameworkId && r.control.kind === "CLAUSE"));
    setDocs(docsData.documents);
    if (latestRun.run?.resultJson) setInsightsMap(buildInsightsMap(latestRun.run.resultJson));
  }

  useEffect(() => {
    loadAll();
    api.get<{ user: { isPlatformOwner: boolean } }>("/auth/me")
      .then((d) => setIsPlatformOwner(d.user.isPlatformOwner ?? false))
      .catch(() => {});
  }, [frameworkId]);

  const withQuestions = (rows ?? []).map((r) =>
    auditQuestions[r.control.id] ? { ...r, control: { ...r.control, guidance: `Key audit question: ${auditQuestions[r.control.id]}` } } : r);

  return (
    <div className="page-pad">
      <StandardHeader frameworkId={frameworkId} page={page} subtitle={subtitle} />
      <PrincipleCards items={principles} />
      <StandardListState loading={rows === null} empty={rows !== null && rows.length === 0} emptyTitle="Clause data not yet available">
        {isPlatformOwner
          ? <>As platform owner, run <code>npm run prisma:seed</code> in the backend directory to load it.</>
          : "Please contact your platform administrator to initialise the reference data."}
      </StandardListState>
      {rows && rows.length > 0 && (
        <FrameworkControlList
          rows={withQuestions}
          docs={docs}
          frameworkId={frameworkId}
          loadAll={loadAll}
          novice={novice}
          insightsMap={insightsMap}
          groups={isoClauseGroups(clauseSummaries, clauseTitles)}
          noun="clause"
          groupLabel="All clauses"
        />
      )}
      <PdcaReference title={pdca.title} accent={frameworkAccent(frameworkId)} phases={pdca.phases} />
    </div>
  );
}
